import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { TranslationService } from '@core/translation/translation.service';
import { AppNotificationService } from '@core/services/app-notification.service';
import { Boat } from '@shared/models/boat';
import { Person } from '@shared/models/person';
import { BoatApiService } from '@bcmServices/api-services/boat.api-service';
import { PersonApiService } from '@bcmServices/api-services/person.api-service';
import { AppDataShareBoatRef, AppDataShareLookupResult, BcmAppDataShareApiService } from '@bcmServices/api-services/app-data-share.api-service';
import { AppDataShareImportService } from './app-data-share-import.service';
import { Up2boatIdBoatPickerDialogComponent } from '@modules/bcm/@shared/components/app-data-share/up2boat-id-boat-picker-dialog.component';

// Gleiches Alphabet/Länge wie die Code-Erzeugung (generate-data-share-code.ts in ym-api,
// ensureUserShareCode in der App-API): ohne I, O, 0 und 1.
const UP2BOAT_ID_PATTERN = /^[ABCDEFGHJKLMNPQRSTUVWXYZ23456789]{6}$/i;

export function isUp2boatId(value: string): boolean {
    return UP2BOAT_ID_PATTERN.test(String(value || '').trim());
}

/**
 * `found: false`: Suchbegriff ist keine bekannte Up2Boat-ID (Muster passt nicht, Code existiert nicht
 * oder Lookup fehlgeschlagen) – Aufrufer verwendet die normale Suche.
 * `found: true`: ID wurde behandelt; `value` ist null bei Abbruch oder bereits angezeigter Warnung.
 */
export type Up2boatIdResolution<T> = { found: false } | { found: true; value: T | null };

const NOT_FOUND: { found: false } = { found: false };

@Injectable({ providedIn: 'root' })
export class Up2boatIdSelectionService {
    constructor(
        private readonly api: BcmAppDataShareApiService,
        private readonly importService: AppDataShareImportService,
        private readonly boatApi: BoatApiService,
        private readonly personApi: PersonApiService,
        private readonly notifications: AppNotificationService,
        private readonly dialog: MatDialog,
    ) {}

    /** Full app-data-share flow (QR scan + Freigabe), then load the tenant person. */
    async resolvePersonFromQr(): Promise<Person | null> {
        try {
            const result = await this.importService.importViaQr('person');
            if (!result?.personId) {
                return null;
            }

            return firstValueFrom(this.personApi.getOne(result.personId));
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    /** Full app-data-share flow (QR scan + Freigabe), then load the tenant boat. */
    async resolveBoatFromQr(): Promise<Boat | null> {
        try {
            const result = await this.importService.importViaQr('boat');
            if (!result) {
                return null;
            }

            const boats: AppDataShareBoatRef[] = result.boats?.length
                ? result.boats
                : (result.boatIds || []).map(id => ({ id }));
            if (!boats.length && result.boatId) {
                boats.push({ id: result.boatId });
            }

            return this.pickBoat(boats);
        } catch (error) {
            console.error(error);
            return null;
        }
    }

    async resolveBoat(raw: string): Promise<Up2boatIdResolution<Boat>> {
        const code = String(raw || '').trim().toUpperCase();

        if (!isUp2boatId(code)) {
            return NOT_FOUND;
        }

        const lookup = await firstValueFrom(this.api.lookupShareCode(code)).catch(() => null);

        if (!lookup || lookup.outcome === 'notFound') {
            return NOT_FOUND;
        }

        return { found: true, value: await this.resolveFoundBoat(code, lookup) };
    }

    private async resolveFoundBoat(code: string, lookup: AppDataShareLookupResult): Promise<Boat | null> {
        try {
            if (lookup.outcome === 'kindMismatch') {
                this.notifications.showWarning(this.mismatchText(lookup.dataType));
                return null;
            }

            if (lookup.outcome === 'inTenant') {
                return this.pickBoat(lookup.boats || []);
            }

            const imported = await this.importService.importShareCode(code, lookup.dataType === 'boat' ? 'boat' : 'person');

            if (!imported) {
                return null;
            }

            const boats = imported.boats?.length
                ? imported.boats
                : (imported.boatIds || []).map(id => ({ id }));
            return this.pickBoat(boats.length ? boats : (imported.boatId ? [{ id: imported.boatId }] : []));
        } catch (error) {
            console.error(error);
            this.notifications.showWarning(TranslationService.translate('person.core.up2boatIdFailed', undefined, 'Datenübernahme fehlgeschlagen.'));
            return null;
        }
    }

    async resolvePerson(raw: string): Promise<Up2boatIdResolution<Person>> {
        const code = String(raw || '').trim().toUpperCase();

        if (!isUp2boatId(code)) {
            return NOT_FOUND;
        }

        const lookup = await firstValueFrom(this.api.lookupShareCode(code, 'person')).catch(() => null);

        if (!lookup || lookup.outcome === 'notFound') {
            return NOT_FOUND;
        }

        return { found: true, value: await this.resolveFoundPerson(code, lookup) };
    }

    private async resolveFoundPerson(code: string, lookup: AppDataShareLookupResult): Promise<Person | null> {
        try {
            if (lookup.outcome === 'kindMismatch' || lookup.dataType === 'boat') {
                this.notifications.showWarning(this.mismatchText('boat'));
                return null;
            }

            if (lookup.outcome === 'inTenant') {
                if (!lookup.personId) {
                    this.notifications.showWarning(TranslationService.translate('person.core.up2boatIdNoPerson', undefined, 'Zu dieser Up2Boat-ID ist keine Person hinterlegt.'));
                    return null;
                }

                return firstValueFrom(this.personApi.getOne(lookup.personId));
            }

            const imported = await this.importService.importShareCode(code, 'person');

            if (!imported?.personId) {
                return null;
            }

            return firstValueFrom(this.personApi.getOne(imported.personId));
        } catch (error) {
            console.error(error);
            this.notifications.showWarning(TranslationService.translate('person.core.up2boatIdFailed', undefined, 'Datenübernahme fehlgeschlagen.'));
            return null;
        }
    }

    private mismatchText(dataType?: 'userProfile' | 'boat'): string {
        if (dataType === 'boat') {
            return TranslationService.translate(
                'person.core.up2boatIdIsBoat',
                undefined,
                'Diese Up2Boat-ID gehört zu einem Boot. Lege das Boot damit an.',
            );
        }

        return TranslationService.translate(
            'person.core.up2boatIdIsPerson',
            undefined,
            'Diese Up2Boat-ID gehört zu einer Person. Lege die Person damit an.',
        );
    }

    private async pickBoat(boats: AppDataShareBoatRef[]): Promise<Boat | null> {
        if (!boats.length) {
            this.notifications.showWarning(TranslationService.translate('person.core.up2boatIdNoBoat', undefined, 'Zu dieser Up2Boat-ID ist kein Boot hinterlegt.'));
            return null;
        }

        let boatId = boats[0].id;

        if (boats.length > 1) {
            const picked = await firstValueFrom(
                this.dialog.open(Up2boatIdBoatPickerDialogComponent, {
                    width: '420px',
                    data: { boats },
                }).afterClosed()
            ) as number | null;

            if (!picked) {
                return null;
            }

            boatId = picked;
        }

        return firstValueFrom(this.boatApi.getOne(boatId));
    }
}
